import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Trash,
  PencilSimple,
  Check,
  X,
  Tag,
  Briefcase,
} from "@phosphor-icons/react";

const ClientDetailPage = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [contactForm, setContactForm] = useState({ name: "", role: "", email: "", phone: "" });
  const [showContact, setShowContact] = useState(false);

  const load = async () => {
    const r = await api.get(`/clients/${id}`);
    setC(r.data);
    setForm(r.data);
  };

  useEffect(() => {
    load();
  }, [id]);

  if (!c) return <div className="label-eyebrow">Loading…</div>;

  const save = async () => {
    try {
      const payload = { ...form };
      if (typeof payload.tags === "string") {
        payload.tags = payload.tags.split(",").map((t) => t.trim()).filter(Boolean);
      }
      const r = await api.patch(`/clients/${id}`, payload);
      setC(r.data);
      setEditing(false);
      toast.success("Client updated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this client? Engagements will be detached but not deleted.")) {
      return;
    }
    await api.delete(`/clients/${id}`);
    toast.success("Client deleted.");
    nav("/clients");
  };

  const addContact = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/clients/${id}/contacts`, contactForm);
      setContactForm({ name: "", role: "", email: "", phone: "" });
      setShowContact(false);
      load();
    } catch {
      toast.error("Could not add contact");
    }
  };

  const removeContact = async (cid) => {
    await api.delete(`/clients/${id}/contacts/${cid}`);
    load();
  };

  return (
    <div data-testid="client-detail-page" className="space-y-8">
      <Link
        to="/clients"
        className="label-eyebrow inline-flex items-center gap-2 hover:text-[#1A2F24]"
      >
        <ArrowLeft size={12} /> Clients
      </Link>

      {/* Header */}
      <div className="pb-8 border-b border-[#1A2F24]/12 flex items-start justify-between gap-6">
        <div className="flex-1 min-w-0">
          <p className="label-eyebrow">{c.type}</p>
          {editing ? (
            <input
              data-testid="edit-name"
              className="input-clean font-serif text-4xl mt-2"
              value={form.name || ""}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          ) : (
            <h1 className="font-serif text-5xl text-[#112018] mt-2 break-words">
              {c.name}
            </h1>
          )}
          {c.tags?.length > 0 && !editing && (
            <div className="flex flex-wrap gap-2 mt-3">
              {c.tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#1A2F24] text-[#F7F6F2] text-[10px] font-mono uppercase tracking-widest"
                >
                  <Tag size={9} /> {t}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {editing ? (
            <>
              <button onClick={save} className="btn-primary" data-testid="save-client">
                <Check size={14} className="inline mr-1" /> Save
              </button>
              <button
                onClick={() => {
                  setEditing(false);
                  setForm(c);
                }}
                className="btn-outline"
              >
                <X size={14} />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setEditing(true)}
                className="btn-outline"
                data-testid="edit-client"
              >
                <PencilSimple size={14} className="inline mr-2" /> Edit
              </button>
              <button
                onClick={remove}
                className="btn-outline hover:!border-[#C94A3D] hover:!text-[#C94A3D]"
                data-testid="delete-client"
              >
                <Trash size={14} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Core details */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="card-flat lg:col-span-1 space-y-4">
          <p className="label-eyebrow">Profile</p>
          <Row label="Email" value={c.email} editing={editing}
            onChange={(v) => setForm({ ...form, email: v })} field={form.email} testid="edit-email" />
          <Row label="Phone" value={c.phone} editing={editing}
            onChange={(v) => setForm({ ...form, phone: v })} field={form.phone} testid="edit-phone" />
          <Row label="Tax ID / EIN" value={c.tax_id} editing={editing}
            onChange={(v) => setForm({ ...form, tax_id: v })} field={form.tax_id} testid="edit-taxid" mono />
          <Row label="Type" value={c.type} editing={editing}
            onChange={(v) => setForm({ ...form, type: v })} field={form.type}
            type="select" options={["individual", "business"]} testid="edit-type" />
          <Row label="Address" value={c.address} editing={editing}
            onChange={(v) => setForm({ ...form, address: v })} field={form.address} testid="edit-address" />
          {editing && (
            <Row label="Tags (comma-sep)"
              value={Array.isArray(c.tags) ? c.tags.join(", ") : c.tags}
              editing onChange={(v) => setForm({ ...form, tags: v })}
              field={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags || ""}
              testid="edit-tags" />
          )}
          <div>
            <p className="label-eyebrow">Notes</p>
            {editing ? (
              <textarea
                data-testid="edit-notes"
                rows={4}
                className="input-clean mt-2"
                value={form.notes || ""}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            ) : (
              <p className="text-sm text-[#1A2F24] mt-1 whitespace-pre-line">
                {c.notes || <span className="italic text-[#606C64]">No notes.</span>}
              </p>
            )}
          </div>
        </div>

        {/* Engagements */}
        <div className="card-flat lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="label-eyebrow">History</p>
              <h2 className="font-serif text-2xl text-[#112018] mt-1">
                Engagements ({c.engagements?.length || 0})
              </h2>
            </div>
          </div>
          {(c.engagements || []).length === 0 ? (
            <div className="text-center py-10 text-sm text-[#606C64]">
              <Briefcase size={28} className="mx-auto text-[#A8977B] mb-3" />
              No engagements yet. Create one from the Engagements page.
            </div>
          ) : (
            <div className="divide-y divide-[#1A2F24]/12">
              {c.engagements.map((e) => (
                <Link
                  key={e.id}
                  to={`/engagements/${e.id}`}
                  className="block py-3 hover:bg-[#F7F6F2] px-2 -mx-2"
                  data-testid={`client-eng-${e.id}`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-[#112018]">{e.service_type}</p>
                      <p className="label-eyebrow mt-1">
                        Created {new Date(e.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-sm text-[#1A2F24]">
                        ${e.fee.toLocaleString()}
                      </p>
                      <p className="label-eyebrow mt-1">{e.status}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Contacts */}
      <div className="card-flat">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="label-eyebrow">Stakeholders</p>
            <h2 className="font-serif text-2xl text-[#112018] mt-1">Contacts</h2>
          </div>
          <button
            onClick={() => setShowContact(!showContact)}
            className="btn-outline"
            data-testid="add-contact-btn"
          >
            <Plus size={12} className="inline mr-2" /> Add Contact
          </button>
        </div>

        {showContact && (
          <form onSubmit={addContact} className="grid md:grid-cols-4 gap-3 mb-6 fade-up">
            <input
              data-testid="contact-name"
              required
              className="input-clean"
              placeholder="Name"
              value={contactForm.name}
              onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
            />
            <input
              data-testid="contact-role"
              className="input-clean"
              placeholder="Role (e.g. CFO)"
              value={contactForm.role}
              onChange={(e) => setContactForm({ ...contactForm, role: e.target.value })}
            />
            <input
              data-testid="contact-email"
              className="input-clean"
              placeholder="Email"
              value={contactForm.email}
              onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
            />
            <div className="flex gap-2">
              <input
                data-testid="contact-phone"
                className="input-clean flex-1"
                placeholder="Phone"
                value={contactForm.phone}
                onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
              />
              <button
                type="submit"
                className="btn-primary"
                data-testid="contact-submit"
              >
                Add
              </button>
            </div>
          </form>
        )}

        {(c.contacts || []).length === 0 ? (
          <p className="text-sm text-[#606C64] italic py-4">No contacts on file.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {c.contacts.map((ct) => (
              <div
                key={ct.id}
                className="border border-[#1A2F24]/12 p-3 flex items-start justify-between"
                data-testid={`contact-${ct.id}`}
              >
                <div>
                  <p className="text-[#112018]">{ct.name}</p>
                  {ct.role && <p className="label-eyebrow mt-1">{ct.role}</p>}
                  {ct.email && (
                    <p className="text-xs text-[#606C64] mt-1 font-mono">{ct.email}</p>
                  )}
                  {ct.phone && (
                    <p className="text-xs text-[#606C64] mt-0.5 font-mono">{ct.phone}</p>
                  )}
                </div>
                <button
                  onClick={() => removeContact(ct.id)}
                  className="p-1 hover:bg-[#efece3]"
                  data-testid={`remove-contact-${ct.id}`}
                >
                  <Trash size={14} className="text-[#606C64]" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Row = ({ label, value, editing, onChange, field, testid, type = "text", options, mono }) => (
  <div>
    <p className="label-eyebrow">{label}</p>
    {editing ? (
      type === "select" ? (
        <select
          data-testid={testid}
          className="input-clean mt-2"
          value={field || ""}
          onChange={(e) => onChange(e.target.value)}
        >
          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      ) : (
        <input
          data-testid={testid}
          className={`input-clean mt-2 ${mono ? "font-mono" : ""}`}
          value={field || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      )
    ) : (
      <p className={`mt-1 ${mono ? "font-mono text-sm" : "text-sm"} text-[#1A2F24]`}>
        {value || <span className="italic text-[#606C64]">—</span>}
      </p>
    )}
  </div>
);

export default ClientDetailPage;
